import { useTranslation } from "react-i18next";
import { Cake, Medal } from "lucide-react";
import { StatusBadge } from "@athena/ui";
import type { MilestoneWhen } from "../helpers/milestones";

type MilestoneBadgeProps = ({ kind: "birthday" } | { kind: "soberAnniversary"; years: number }) & {
    when?: MilestoneWhen;
    /** `sm` beside another status badge, as in the profile header, so the two sit as a pair. */
    size?: "xs" | "sm";
};

/**
 * Marks a milestone that falls today or a week from today: a birthday, or a sober anniversary and
 * which one. The color says which kind — a birthday is the brand's teal rather than amber, which
 * "needs outreach" already wears, and an anniversary green — and the words say when.
 */
export function MilestoneBadge({ when = "today", size = "xs", ...props }: MilestoneBadgeProps) {
    const { t } = useTranslation();
    const birthday = props.kind === "birthday";
    const Icon = birthday ? Cake : Medal;

    let label: string;
    if (props.kind === "birthday") {
        label =
            when === "today"
                ? t("admin.alumni.milestones.birthdayToday")
                : t("admin.alumni.milestones.birthdayInAWeek");
    } else {
        label =
            when === "today"
                ? t("admin.alumni.milestones.soberToday", { count: props.years })
                : t("admin.alumni.milestones.soberInAWeek", {
                      years: t("admin.alumni.milestones.years", { count: props.years }),
                  });
    }

    return (
        <StatusBadge
            variant={birthday ? "brand" : "success"}
            size={size}
            className="shrink-0"
            textClassName="inline-flex items-center gap-1"
        >
            <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3"} aria-hidden />
            {label}
        </StatusBadge>
    );
}
